import { Node as ProseMirrorNode } from '@tiptap/pm/model';
import { TableMap } from '@tiptap/pm/tables';

// TABLE sections have both a header row (row 0) and a header column (col 0,
// rows 1+): the header column must always survive, so at least one more
// column is required besides it. RECORD sections only have a header row, so
// every column is a regular column — just keep at least one column overall.
const MIN_COLUMNS_WITH_HEADER_COLUMN = 2;
const MIN_COLUMNS_WITHOUT_HEADER_COLUMN = 1;
// Both TABLE and RECORD sections need at least one data row besides the
// header row.
const MIN_ROWS = 2;

/**
 * A TABLE section's (0,0) cell is a COLUMN-type header and its (1,0) cell is
 * a ROW-type header — that combination is what marks column 0 as a header
 * column. RECORD sections never have a ROW-type header, so this is false.
 *
 * Compares against the raw 'ROW'/'COLUMN' strings (mirroring TableCellType
 * from module/ui/.../table-toolbar) rather than importing that enum, to keep
 * this module dependency-free — it's imported from both that file and from
 * table-header/extension.tsx, so importing back from either would risk a
 * circular dependency.
 */
export const tableHasHeaderColumn = (tableNode: ProseMirrorNode): boolean => {
  const map = TableMap.get(tableNode);

  if (map.height < 2) {
    return false;
  }

  const firstColHeaderNode = tableNode.nodeAt(map.positionAt(0, 0, tableNode));
  const secondRowHeaderNode = tableNode.nodeAt(map.positionAt(1, 0, tableNode));

  return (
    firstColHeaderNode?.attrs?.config?.cellType === 'COLUMN' &&
    secondRowHeaderNode?.attrs?.config?.cellType === 'ROW'
  );
};

/**
 * The trailing "+ Add a column" affordance is implemented as a real column
 * in the doc (a tableHeader with `ghost: true`) so it doesn't count towards
 * the actual column total for the min-columns rule below.
 */
const countRealColumns = (tableNode: ProseMirrorNode): number => {
  const map = TableMap.get(tableNode);
  let count = 0;

  for (let col = 0; col < map.width; col++) {
    const headerNode = tableNode.nodeAt(map.positionAt(0, col, tableNode));

    if (headerNode?.attrs?.ghost !== true) {
      count += 1;
    }
  }

  return count;
};

/**
 * Whether a column may be deleted from a TABLE/RECORD table.
 * `isTargetingHeaderColumn` must be supplied by the caller — resolving which
 * column is targeted depends on position-math conventions that differ
 * between call sites (NodeView getPos() vs. selection-resolved cell start),
 * so it's deliberately not recomputed here.
 */
export const canDeleteColumn = (
  tableNode: ProseMirrorNode,
  isTargetingHeaderColumn = false,
): boolean => {
  if (isTargetingHeaderColumn) {
    return false;
  }

  const minColumns = tableHasHeaderColumn(tableNode)
    ? MIN_COLUMNS_WITH_HEADER_COLUMN
    : MIN_COLUMNS_WITHOUT_HEADER_COLUMN;

  return countRealColumns(tableNode) > minColumns;
};

/** Whether a row may be deleted from a TABLE/RECORD table — independent of
 * which row, since only the count of remaining rows matters. */
export const canDeleteRow = (tableNode: ProseMirrorNode): boolean =>
  TableMap.get(tableNode).height > MIN_ROWS;
